import type {
  AdminProductCategoriesResponse,
  AdminProductCategory,
  AdminProductCategoryField,
  AdminProductCategoryFieldsResponse,
  CreateAdminProductCategoryBody,
  CreateAdminProductCategoryFieldBody,
  PatchAdminProductCategoryBody,
  UpdateAdminProductCategoryBody,
  UpdateAdminProductCategoryFieldBody,
} from '@/api/(admin)/product-categories/types';
import { API_ROOT_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

/**
 * Pure RPC over the admin endpoints for both halves of this feature: the
 * categories and the fields each category owns. Every call is migrated to
 * `/api/admin` and takes `API_ROOT_URL`.
 */

// ─── Categories ──────────────────────────────────────────────────────────────

/**
 * The list. Takes a pre-built query string; paging, sort and filter live in the
 * hook. Migrated to `/api/admin/product-categories`, so it takes
 * `API_ROOT_URL`; answers `{ data, metadata: { pagination } }`.
 */
export async function fetchAdminProductCategories(
  qs: string,
): Promise<AdminProductCategoriesResponse> {
  return apiFetch<AdminProductCategoriesResponse>(
    `/api/admin/product-categories?${qs}`,
    undefined,
    API_ROOT_URL,
  );
}

/**
 * One category, including its embedded facility processes. Migrated to
 * `/api/admin/product-categories/:id`, so it takes `API_ROOT_URL`; answers the
 * `{ data, metadata }` envelope as-is.
 */
export async function fetchAdminProductCategory(
  id: string,
): Promise<{ data: AdminProductCategory }> {
  return apiFetch<{ data: AdminProductCategory }>(
    `/api/admin/product-categories/${encodeURIComponent(id)}`,
    undefined,
    API_ROOT_URL,
  );
}

/**
 * Migrated to `/api/admin/product-categories` (answers 201), so it takes
 * `API_ROOT_URL`. Answers the `{ data, metadata }` envelope as-is.
 */
export async function createAdminProductCategory(
  body: CreateAdminProductCategoryBody,
): Promise<{ data: AdminProductCategory }> {
  return apiFetch<{ data: AdminProductCategory }>(
    '/api/admin/product-categories',
    { method: 'POST', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

/**
 * A replace; will not move a category to another parent. Migrated to
 * `/api/admin/product-categories/:id`, so it takes `API_ROOT_URL`; answers the
 * `{ data, metadata }` envelope as-is.
 */
export async function updateAdminProductCategory(
  id: string,
  body: UpdateAdminProductCategoryBody,
): Promise<{ data: AdminProductCategory }> {
  return apiFetch<{ data: AdminProductCategory }>(
    `/api/admin/product-categories/${encodeURIComponent(id)}`,
    { method: 'PUT', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

/**
 * A partial update. The only write that carries `isWeightAvailable`, so it is
 * how that flag is set. Migrated to `/api/admin/product-categories/:id`, so it takes `API_ROOT_URL`;
 * answers the `{ data, metadata }` envelope as-is.
 */
export async function patchAdminProductCategory(
  id: string,
  body: PatchAdminProductCategoryBody,
): Promise<{ data: AdminProductCategory }> {
  return apiFetch<{ data: AdminProductCategory }>(
    `/api/admin/product-categories/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

// ─── Fields ──────────────────────────────────────────────────────────────────

/**
 * One category's fields. The category is selected through the `filter` param.
 * Migrated to `/api/admin/product-category-fields`, so it takes `API_ROOT_URL`.
 */
export async function fetchAdminProductCategoryFields(
  qs: string,
): Promise<AdminProductCategoryFieldsResponse> {
  return apiFetch<AdminProductCategoryFieldsResponse>(
    `/api/admin/product-category-fields?${qs}`,
    undefined,
    API_ROOT_URL,
  );
}

/**
 * Migrated to `/api/admin/product-category-fields` (answers 201), so it takes
 * `API_ROOT_URL`. Answers the `{ data, metadata }` envelope as-is.
 */
export async function createAdminProductCategoryField(
  body: CreateAdminProductCategoryFieldBody,
): Promise<{ data: AdminProductCategoryField }> {
  return apiFetch<{ data: AdminProductCategoryField }>(
    '/api/admin/product-category-fields',
    { method: 'POST', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

/**
 * Migrated to `/api/admin/product-category-fields/:id`, so it takes
 * `API_ROOT_URL`; `apiFetch`'s default base would put `/api-service` in front
 * of the path. Answers the `{ data, metadata }` envelope as-is.
 */
export async function updateAdminProductCategoryField(
  id: string,
  body: UpdateAdminProductCategoryFieldBody,
): Promise<{ data: AdminProductCategoryField }> {
  return apiFetch<{ data: AdminProductCategoryField }>(
    `/api/admin/product-category-fields/${encodeURIComponent(id)}`,
    { method: 'PUT', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

/**
 * Deletes one. The only delete in the management area's migrated pages.
 * Migrated to `/api/admin/product-category-fields/:id` (answers 204), so it
 * takes `API_ROOT_URL`.
 */
export async function deleteAdminProductCategoryField(
  id: string,
): Promise<void> {
  return apiFetch<void>(
    `/api/admin/product-category-fields/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    API_ROOT_URL,
  );
}
